import React, { Component } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import QuillEditor from '../components/QuillEditor'
import UpgradePopup from '../components/UpgradePopup'
import DateTimePicker from '../utils/DateTimePicker'
import isPaidUser from '../utils/isPaidUser'
import { withRouter } from '../utils/routerCompat'
import { createSaveQueue } from '../utils/saveQueue'
import { newId } from '../utils/newId'
import { alreadyExists } from '../utils/alreadyExists'
import { refusalOf, MEMBERS_ONLY, publishFailedMessage } from '../utils/refusal'
import { countWords } from '../components/QuillEditor'

import {
  saveJournal,
  updateJournal,
  adjustBookJournalsCount,
} from '../actions/journal'

import ReactGA from 'react-ga4'

export class WriteJournal extends Component {
  constructor(props) {
    super(props)
    this.state = {
      privacy: 'PRIVATE',
      journalUid: '',
      journalAuthorId:
        props.user.accountData.idToken.payload['cognito:username'],
      journalContent: '',
      journalWordCount: 0,
      showUpgrade: false,
      journalBookId: '',
      journalPromptCheck: false,
      journalCreatedAt: new Date(),
      upgradePopUpMessage: {
        featureName: 'Publishing a public journal',
        ctaText: 'Publish it as a private journal',
      },
      isSavingJournal: false,
      saveError: '',
    }
    this.editor = React.createRef()
    // Saves run one at a time; see utils/saveQueue.js (review D1).
    this.saves = createSaveQueue((err) => {
      console.log(err)
      this.setState({ saveError: "Your draft couldn't be saved. Check your connection; it saves again as you type." })
    })
    // The journal's ID once created, and the newest text from the editor. Kept outside state
    // so a save always reads the current values (setState is asynchronous).
    this.journalId = ''
    this.latest = null
    // The ID the new journal is created with, chosen here: a create retried after a lost
    // response finds its own row instead of creating a second journal.
    this.newJournalId = newId()
    this.publishing = false
    this.unmounted = false
  }

  componentWillUnmount() {
    // Leaving the page: the last typing and the waiting draft are still saved.
    this.unmounted = true
    this.editor.current?.flush()
    this.saves.drain()
  }

  async componentDidMount() {
    const params = new URLSearchParams(this.props.history?.location?.search)
    const bookSelected = params.get('bookSelected')
    const journalPrompt = params.get('journalPrompt')

    bookSelected && this.setState({ journalBookId: bookSelected })
    journalPrompt &&
      this.setState({
        journalContent:
          '<h1><span style="color: rgb(60, 206, 102);">' +
          journalPrompt +
          '</span></h1>',
      })

    this.setState({ journalPromptCheck: true })
  }

  _selectPrivacy = async (e) => {
    this.setState({
      privacy: e.target.value,
    })
  }

  _isPaid = () => isPaidUser(this.props.user.currentUser.attributes['custom:membership'])

  // Free users' journals are always private, drafts included.
  _privacyFor = (privacy) => (this._isPaid() ? privacy : 'PRIVATE')

  _handleChange = (textPassed, wordCount) => {
    this.latest = { content: textPassed, journalWordCount: wordCount }
    if (!this.unmounted) {
      this.setState({
        journalContent: textPassed,
        journalWordCount: wordCount,
      })
    }
    // While publishing, the text only goes into this.latest (Publish sends it).
    if (textPassed.length < 10 || this.saves.closed) return

    // Free users: no autosave; the notice shows once, at the first save after the first draft.
    if (this.journalId && !this._isPaid()) {
      // Shown once; never used up while the page is closing.
      if (!this.unmounted && localStorage.getItem('auto-save-for-paid-users') !== 'true') {
        this.setState({
          showUpgrade: true,
          upgradePopUpMessage: { featureName: 'Autosave', ctaText: 'Close' },
        })
        localStorage.setItem('auto-save-for-paid-users', 'true')
      }
      return
    }
    this.saves.draft(this._saveDraft)
  }

  // Creates the journal on the first save, then updates it. Runs from the save queue, so it
  // reads the newest text and never runs twice at once.
  _saveDraft = async () => {
    // A change that queued while the free user's first draft was being created.
    if (this.journalId && !this._isPaid()) return
    const { privacy, journalAuthorId, journalBookId, journalCreatedAt } = this.state
    const draft = {
      journalAuthorId,
      privacy: this._privacyFor(privacy),
      draft: true,
      ...this.latest,
    }
    if (this.journalId) {
      await this.props.updateJournal({ ...draft, id: this.journalId, journalBookId })
    } else {
      this._adopt(await this._create({ ...draft, createdAt: journalCreatedAt.toISOString() }))
    }
    if (this.state.saveError) this.setState({ saveError: '' })
  }

  _create = async (journal) => {
    try {
      return await this.props.saveJournal({ ...journal, id: this.newJournalId })
    } catch (err) {
      // An earlier try already created it (its response was lost).
      if (alreadyExists(err)) return { id: this.newJournalId }
      throw err
    }
  }

  // The new journal's ID comes from the create's own result, never from currentJournal.
  _adopt = (created) => {
    if (!created?.id) throw new Error('the journal was not created')
    this.journalId = created.id
    if (this.unmounted) return
    this.setState({ journalUid: created.id })
    // Replacing URL with the newly created journal ID so it won't mess around with other journals when auto save is triggered
    window.history.replaceState(null, ``, `/edit/${created.id}`)
  }

  _publishJournal = async () => {
    const { privacy } = this.state
    const { user } = this.props
    if (
      privacy === 'PUBLIC' &&
      !isPaidUser(user.currentUser.attributes['custom:membership'])
    ) {
      this.setState({
        showUpgrade: true,
        upgradePopUpMessage: {
          featureName: 'Publishing a public journal',
          ctaText: 'Publish it as a private journal',
        },
      })
    } else {
      this._updateJournal(privacy)
      // Event tracking.
      ReactGA.event({
        category: this.state.journalAuthorId,
        action: 'Writing',
        label: privacy,
      })
    }
  }

  _popupControl = async (callback) => {
    this.setState({
      showUpgrade: callback,
    })
  }

  _updateJournal = async (privacy) => {
    if (this.publishing) return
    this.publishing = true
    // No more drafts (the waiting one is dropped, the one in flight finishes), then the last
    // half second of typing, which only updates this.latest now.
    const closing = this.saves.close()
    this.editor.current?.flush()
    this.setState({ isSavingJournal: true })
    await closing

    const { journalAuthorId, journalContent, journalBookId, journalCreatedAt } = this.state
    const text = this.latest ?? { content: journalContent, journalWordCount: countWords(journalContent) }
    // The same minimum as the Publish button (the popups call this directly).
    if (!text.content || text.content.length < 10) {
      this.publishing = false
      this.saves.reopen()
      this.setState({ isSavingJournal: false })
      return
    }

    try {
      // Publishing before the first draft was saved: create it first.
      if (!this.journalId) {
        this._adopt(
          await this._create({
            journalAuthorId,
            privacy: this._privacyFor(privacy),
            draft: true,
            ...text,
            createdAt: journalCreatedAt.toISOString(),
          })
        )
      }
      const journalUid = this.journalId
      await this.props.updateJournal({
        id: journalUid,
        privacy: privacy,
        journalAuthorId: journalAuthorId,
        draft: false,
        ...text,
        journalBookId: journalBookId,
        createdAt: journalCreatedAt.toISOString(),
      })
      // Update book bookJournalCount number if journalBookId exist
      if (journalBookId && journalBookId !== null) {
        await this.props.adjustBookJournalsCount(journalBookId, 1)
      }
      this.props.history.push({
        pathname:
          (privacy === 'PUBLIC' ? '/public-journal/' : '/journal/') +
          journalUid,
        state: { prevPath: this.props.location.pathname },
      })
    } catch (err) {
      console.log(err)
      this.publishing = false
      this.saves.reopen()
      if (refusalOf(err) === MEMBERS_ONLY) {
        // The server says the membership has lapsed: offer to publish it privately instead.
        this.setState({
          isSavingJournal: false,
          saveError: '',
          showUpgrade: true,
          upgradePopUpMessage: {
            featureName: 'Publishing a public journal',
            ctaText: 'Publish it as a private journal',
          },
        })
        return
      }
      this.setState({
        isSavingJournal: false,
        saveError: publishFailedMessage(err),
      })
    }
  }

  _selectBook = async (e) => {
    this.setState({
      journalBookId: e.target.value,
    })
  }

  _newJournalCreatedAt = (date) => {
    const { journalCreatedAt } = this.state
    const { user } = this.props
    if (!isPaidUser(user.currentUser.attributes['custom:membership'])) {
      this.setState({
        showUpgrade: true,
        journalCreatedAt: journalCreatedAt,
        upgradePopUpMessage: {
          featureName: 'Changing date and time',
          ctaText: 'Publish it with current date and time',
        },
      })
    } else {
      this.setState({
        journalCreatedAt: date,
      })
    }
  }

  render() {
    const {
      privacy,
      showUpgrade,
      journalBookId,
      journalContent,
      journalPromptCheck,
      upgradePopUpMessage,
      journalCreatedAt,
      isSavingJournal,
      saveError,
    } = this.state
    const { journal, user } = this.props

    const bookList =
      user && user.userBooks.length > 0 ? (
        <select
          className="select-new book-select"
          onChange={this._selectBook}
          value={journalBookId}
          id="writeJournal_book_select"
        >
          <option value="">No book</option>
          {user.userBooks.map((book) => {
            return (
              <option key={book.id} value={book.id}>
                {book.book}
              </option>
            )
          })}
        </select>
      ) : (
        <select className="select-new book-select">
          <option value="">No book</option>
        </select>
      )

    const disableSaveJournal = journal.pendingCreate || journal.pendingUpdate || isSavingJournal;

    return (
      <div className="write-journal">
        <ul className="sub-nav">
          <li>
            <span
              onClick={() => this.props.history.goBack()}
              className="link-text"
              id="writeJournal_go_back"
            >
              <ion-icon name="arrow-round-back"></ion-icon> Back
            </span>
          </li>
          <li>{bookList}</li>
          <li>
            <button
              onClick={this._publishJournal}
              className="link-btn"
              id="writeJournal_publish_button"
              disabled={disableSaveJournal || journalContent.length < 10}

            >
              {disableSaveJournal ? 'Saving...' : 'Publish'}
            </button>
          </li>
        </ul>
        <div className="journal-option">
          <DateTimePicker
            setDate={journalCreatedAt}
            newDate={this._newJournalCreatedAt}
          />
          <select
            className="select-new"
            onChange={this._selectPrivacy}
            value={privacy}
            id="writeJournal_privacy_select"
          >
            <option value="PRIVATE">Private</option>
            <option value="PUBLIC">Public</option>
          </select>
        </div>
        {saveError && (
          <p className="error-msg" role="alert">
            {saveError}
          </p>
        )}
        <div className="Container write-editor">
          {journalPromptCheck && (
            <QuillEditor
              ref={this.editor}
              value={journalContent}
              textCallback={this._handleChange}
            />
          )}
        </div>

        <UpgradePopup
          featureName={upgradePopUpMessage.featureName}
          ctaText={upgradePopUpMessage.ctaText}
          showUpgrade={showUpgrade}
          popupControl={this._popupControl}
          publishPrivate={this._updateJournal}
        />
      </div>
    )
  }
}

function mapStateToProps(state) {
  return state
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      saveJournal,
      updateJournal,
      adjustBookJournalsCount,
    },
    dispatch
  )
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(WriteJournal))
